import { css } from "@emotion/react"
import styled from "@emotion/styled"
import { Side } from "./types/common"
import { FlexContainer } from "../../../../styled/flexbox.styled"
import { SCIENT_COLORS } from "../../../../styled/scientColors"

// Main container for the entire grid layout
export const TickerGridContainer = styled(FlexContainer)({
  width: "100%",
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gridTemplateRows: "auto auto",
  gap: "8px",
  padding: "8px",
  minHeight: "144px",
  "@media (max-width: 768px)": {
    gridTemplateColumns: "1fr",
    gridTemplateRows: "auto auto auto auto",
    minHeight: "160px",
  },
})

interface SectionProps {
  type: Side
  isCollapsed?: boolean
}

// Colors for each section based on side
const getSectionColor = (side: Side, lighter = false) => {
  switch (side) {
    case Side.BUY:
      return lighter ? SCIENT_COLORS.green4 : SCIENT_COLORS.green3
    case Side.BUY_COVER:
      return lighter ? SCIENT_COLORS.green4 : SCIENT_COLORS.forest2
    case Side.SELL:
      return lighter ? SCIENT_COLORS.red3 : SCIENT_COLORS.red1
    case Side.SELL_SHORT:
      return lighter ? SCIENT_COLORS.red3 : SCIENT_COLORS.vermilion3
    default:
      return SCIENT_COLORS.darkGray3
  }
}

// Section container for each quadrant
export const SectionContainer = styled(FlexContainer)<SectionProps>`
  flex-direction: column;
  border-radius: 6px;
  border: 1px solid ${props => getSectionColor(props.type, true)};
  background-color: ${SCIENT_COLORS.darkGray4};
  overflow: hidden;
  transition: all 0.2s ease-in-out;
  min-height: ${props => (props.isCollapsed ? "36px" : "200px")};
  max-height: ${props => (props.isCollapsed ? "36px" : "400px")};
  height: ${props => (props.isCollapsed ? "36px" : "auto")};
`

// Header for each section
export const SectionHeader = styled(FlexContainer)<{ type: Side }>`
  background-color: ${SCIENT_COLORS.darkGray4};
  border-bottom: 1px solid ${props => getSectionColor(props.type, true)};
  padding: 4px 12px;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  height: 36px;
`

// Badge for the action type
export const ActionBadge = styled.div<{ type: Side }>`
  background-color: transparent;
  padding: 2px 8px;
  margin-right: 8px;
  font-weight: bold;
  font-size: 12px;
  display: flex;
  align-items: center;
  color: white;
`

// Count indicator for tickers
export const CountIndicator = styled.div`
  background-color: ${SCIENT_COLORS.darkGray2};
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 12px;
  color: ${SCIENT_COLORS.gray4};
  margin-left: 8px;
`

// Content area for the tickers
export const TickersContent = styled(FlexContainer)<{ isCollapsed?: boolean }>`
  flex-direction: column;
  padding: ${props => (props.isCollapsed ? "0" : "12px")};
  overflow-y: auto;
  flex-grow: 1;
  max-height: ${props => (props.isCollapsed ? "0" : "350px")};
  transition: all 0.2s ease-in-out;
  opacity: ${props => (props.isCollapsed ? "0" : "1")};
  visibility: ${props => (props.isCollapsed ? "hidden" : "visible")};
`

// Grid container for the ticker items
export const TickersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  gap: 8px;
  width: 100%;
`

// Ticker item
export const TickerItem = styled.div<{ type: Side }>`
  background-color: ${SCIENT_COLORS.darkGray3};
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 13px;
  font-weight: 500;
  color: white;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-left: 3px solid ${props => getSectionColor(props.type, true)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background-color: ${SCIENT_COLORS.darkGray2};
  }
`

// Controls container for the rotate icon
export const ControlsContainer = styled.div`
  display: flex;
  align-items: center;
`

// Caret icon with rotation
export const RotatingCaret = styled.div<{ isCollapsed?: boolean }>`
  transition: transform 0.2s ease-in-out;
  transform: ${props => (props.isCollapsed ? "rotate(-90deg)" : "rotate(0deg)")};
`

export const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  font-weight: 500;
  color: white;
`

export const NoItems = styled.div`
  color: ${SCIENT_COLORS.gray3};
  font-size: 13px;
  text-align: center;
  padding: 16px 0;
  width: 100%;
`
